import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SectionCard, SectionHeader, SectionLoading } from './ui/Section';
import { useParams } from 'react-router-dom';
import { useBestTimeToPost } from '../hooks/useBestTimeToPost';

interface BestTimeToPostProps {
  selectedPillars: string[];
  selectedBrands: string[];
  startDate: string | null;
  endDate: string | null;
}

const BestTimeToPost: React.FC<BestTimeToPostProps> = ({
  selectedPillars = [],
  selectedBrands = [],
  startDate = null,
  endDate = null,
}) => {
  const theme = useTheme();
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Fetch data using our custom hook
  const { data, loading, error } = useBestTimeToPost(
    orgId,
    selectedPillars,
    startDate,
    endDate,
    selectedBrands
  );
  
  // Calculate the maximum engagement value for color scaling
  const maxEngagement = useMemo(() => {
    if (!data || !data.heatmap_data) return 0;
    
    let max = 0;
    data.heatmap_data.forEach(day => {
      day.hours.forEach(hour => {
        if (hour.value > max) max = hour.value;
      });
    });
    
    return max;
  }, [data]);
  
  // Five-step plum ramp keyed to share of the busiest slot
  const RAMP = [0.12, 0.3, 0.5, 0.72, 0.95];
  const emptyCell = theme.palette.mode === 'dark' ? alpha('#FFFFFF', 0.04) : alpha(theme.palette.text.primary, 0.04);
  const getColorIntensity = (value: number) => {
    if (maxEngagement === 0 || value === 0) return emptyCell;
    const percentage = value / maxEngagement;
    const step = Math.min(RAMP.length - 1, Math.floor(percentage * RAMP.length));
    return alpha(theme.palette.primary.main, RAMP[step]);
  };

  // Format time for display (e.g., "9 AM", "2 PM")
  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  const header = (
    <SectionHeader
      title="Best Time to Post"
      info="Average engagement for posts published in each hour of the week. Numbers in the cells are post counts; darker cells earned more engagement."
      actions={data?.timezone ? (
        <Typography variant="body2" color="text.secondary">
          Times in {data.timezone}
        </Typography>
      ) : undefined}
    />
  );

  // Loading state
  if (loading && !data) {
    return (
      <SectionCard>
        {header}
        <SectionLoading height={320} />
      </SectionCard>
    );
  }

  // Error state
  if (error) {
    return (
      <SectionCard>
        {header}
        <Alert severity="error">Best time to post didn't load: {error}</Alert>
      </SectionCard>
    );
  }

  // No data state
  if (!data || !data.heatmap_data || data.heatmap_data.length === 0) {
    return (
      <SectionCard>
        {header}
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          No time-of-day data for these filters. This happens when posts don't include the hour they went out.
        </Typography>
      </SectionCard>
    );
  }

  const DAY_LABEL_WIDTH = 48;

  return (
    <SectionCard>
      {header}

      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${DAY_LABEL_WIDTH}px repeat(24, minmax(26px, 1fr))`,
            gap: '3px',
            minWidth: 720,
          }}
        >
          {/* Hour labels: every third hour keeps the axis readable */}
          <Box />
          {[...Array(24)].map((_, i) => (
            <Box
              key={`hour-${i}`}
              sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: 22 }}
            >
              {i % 3 === 0 && (
                <Typography variant="caption" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                  {formatHour(i)}
                </Typography>
              )}
            </Box>
          ))}

          {/* Day rows and heatmap cells */}
          {data.heatmap_data.map((day, dayIndex) => (
            <React.Fragment key={day.day}>
              <Box sx={{ gridColumn: '1 / 2', gridRow: `${dayIndex + 2}`, display: 'flex', alignItems: 'center', pr: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} title={day.day}>
                  {day.day.slice(0, 3)}
                </Typography>
              </Box>

              {day.hours.map((hour) => {
                const strong = maxEngagement > 0 && hour.value / maxEngagement >= 0.6;
                return (
                  <Box
                    key={`${day.day}-${hour.hour}`}
                    sx={{
                      gridColumn: `${hour.hour + 2} / ${hour.hour + 3}`,
                      gridRow: `${dayIndex + 2}`,
                      height: 34,
                      borderRadius: '5px',
                      backgroundColor: getColorIntensity(hour.value),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'default',
                      transition: 'box-shadow 120ms ease',
                      '&:hover': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
                    }}
                    title={`${day.day} at ${formatHour(hour.hour)}: ${hour.value.toFixed(1)} avg engagement (${hour.count} posts)`}
                  >
                    {hour.count > 0 && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                          color: strong ? (theme.palette.mode === 'dark' ? '#1B1526' : '#FFFFFF') : 'text.primary',
                        }}
                      >
                        {hour.count}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
        <Typography variant="caption">Lower engagement</Typography>
        <Box sx={{ display: 'flex', gap: '3px' }}>
          {RAMP.map((a) => (
            <Box key={a} sx={{ width: 22, height: 12, borderRadius: '3px', bgcolor: alpha(theme.palette.primary.main, a) }} />
          ))}
        </Box>
        <Typography variant="caption">Higher engagement</Typography>
        <Typography variant="caption" sx={{ ml: { sm: 'auto' } }}>
          Numbers are post counts.
        </Typography>
      </Box>
    </SectionCard>
  );
};

export default BestTimeToPost; 